import React, { useEffect, useRef, useState } from "react";
import { NavLink, Outlet, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { BookOpen, Users, MusicNote, PaperPlaneTilt, Info as InfoIcon, Bell, GearSix, SignOut, SignIn, UserCircle, ShieldStar, CloudSlash, Rewind, ChatTeardropDots } from "@phosphor-icons/react";
import { useAuth } from "@/contexts/AuthContext";
import { useOnline } from "@/hooks/useOnline";
import { usePushAutoPrompt } from "@/hooks/usePushAutoPrompt";
import { isConferenceOver } from "@/lib/conference";
import InstallPrompt from "@/components/InstallPrompt";
import TopBanner from "@/components/TopBanner";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";

const NAV_BASE = [
  { to: "/program", label: "Programme", icon: BookOpen, testid: "nav-program" },
  { to: "/watch", label: "Replays", icon: Rewind, testid: "nav-watch" },
  { to: "/speakers", label: "Speakers", icon: Users, testid: "nav-speakers" },
  // Slot 4 is dynamic: Playlist by default, Feedback when admin publishes feedback.
  { to: "/playlist", label: "Playlist", icon: MusicNote, testid: "nav-playlist" },
  { to: "/networking", label: "Network", icon: PaperPlaneTilt, testid: "nav-networking" },
  { to: "/info", label: "Info", icon: InfoIcon, testid: "nav-info" },
];
const FEEDBACK_NAV_ITEM = { to: "/feedback", label: "Feedback", icon: ChatTeardropDots, testid: "nav-feedback" };

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [unreadDM, setUnreadDM] = useState(0);
  const [feedbackPublished, setFeedbackPublished] = useState(false);
  const online = useOnline();
  const prevOnline = useRef(online);

  // First-open auto-prompt for push notifications - default to ON unless the
  // delegate explicitly declines. They can always toggle via Info → Notifications.
  usePushAutoPrompt(user);

  useEffect(() => {
    // Notify delegates when we recover from an offline state so they know their
    // optimistic Q&A posts / poll votes just synced.
    if (prevOnline.current === false && online === true) {
      toast.success("Back online - changes synced", { duration: 2000 });
    }
    prevOnline.current = online;
  }, [online]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 6);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Poll the unread-DM count for the Network nav badge. Networking is on by
  // default for every signed-in delegate; only skip polling for users who
  // explicitly turned themselves invisible (they still have existing threads
  // but won't receive new DMs).
  useEffect(() => {
    if (!user) { setUnreadDM(0); return; }
    let active = true;
    const refresh = async () => {
      try {
        const { default: api } = await import("@/api");
        const { data } = await api.get("/networking/unread-count");
        if (active) setUnreadDM(data.unread || 0);
      } catch { /* ignore - the badge is best-effort */ }
    };
    refresh();
    // Refresh on focus + on a slow interval while the tab is visible. Once
    // the conference is over there is no live event driving DMs, so we stop
    // polling entirely and rely on the focus listener.
    const onFocus = () => refresh();
    window.addEventListener("focus", onFocus);
    const intervalMs = isConferenceOver() ? 0 : 5 * 60 * 1000;
    const interval = intervalMs > 0 ? setInterval(() => {
      if (document.visibilityState === "visible") refresh();
    }, intervalMs) : null;
    return () => {
      active = false;
      window.removeEventListener("focus", onFocus);
      if (interval) clearInterval(interval);
    };
  }, [user?.id]);

  // Poll the feedback-published flag so the bottom-nav slot 4 swaps from
  // Playlist to Feedback the moment an admin hits Publish on the day.
  useEffect(() => {
    if (!user) { setFeedbackPublished(false); return; }
    let active = true;
    const refresh = async () => {
      try {
        const { default: api } = await import("@/api");
        const { data } = await api.get("/feedback/config");
        if (active) setFeedbackPublished(!!data.published);
      } catch { /* best-effort */ }
    };
    refresh();
    const onFocus = () => refresh();
    window.addEventListener("focus", onFocus);
    const intervalMs = isConferenceOver() ? 0 : 5 * 60 * 1000;
    const interval = intervalMs > 0 ? setInterval(() => {
      if (document.visibilityState === "visible") refresh();
    }, intervalMs) : null;
    return () => {
      active = false;
      window.removeEventListener("focus", onFocus);
      if (interval) clearInterval(interval);
    };
  }, [user?.id]);

  const NAV = feedbackPublished
    ? NAV_BASE.map((n) => (n.to === "/playlist" ? FEEDBACK_NAV_ITEM : n))
    : NAV_BASE;

  return (
    <div className="min-h-screen relative">
      <TopBanner />
      <header
        data-testid="app-header"
        className={`sticky top-0 z-40 bg-[#FFFFFF]/85 backdrop-blur-xl border-b transition-all ${scrolled ? "border-[#E5E9F0]" : "border-transparent"}`}
      >
        <div className="max-w-3xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          <Link to="/program" className="flex items-center gap-2 group" data-testid="app-logo">
            <img src="/dftb26-mark.png" alt="" className="h-7 w-7 object-contain" />
            <img src="/dftb26-logo-dark.png" alt="DFTB26" className="h-6 w-auto object-contain hidden sm:block" />
            <span className="font-display text-lg tracking-tight sm:hidden">DFTB<span className="text-[#43C4E3]">26</span></span>
          </Link>

          <div className="flex items-center gap-1">
            {!online && (
              <span
                data-testid="offline-pill"
                role="status"
                aria-live="polite"
                className="hidden sm:inline-flex items-center gap-1.5 h-8 px-2.5 mr-1 rounded-sm border border-[#000000]/40 bg-[#000000]/10 text-[11px] font-mono-time uppercase tracking-widest text-[#000000]"
                title="You're offline - showing cached programme. Changes will sync when you're back online."
              >
                <CloudSlash size={13} weight="bold" />
                Offline
              </span>
            )}
            {!online && (
              <span
                data-testid="offline-dot"
                aria-hidden="true"
                className="sm:hidden w-2 h-2 rounded-full bg-[#000000] mr-1"
                title="Offline - showing cached data"
              />
            )}
            <button
              onClick={() => navigate("/announcements")}
              data-testid="header-bell-btn"
              aria-label="Announcements"
              className="w-11 h-11 grid place-items-center rounded-sm hover:bg-[#FFFFFF] transition-colors"
            >
              <Bell size={20} weight="regular" />
            </button>

            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    data-testid="header-menu-btn"
                    aria-label="Account menu"
                    className="w-11 h-11 grid place-items-center rounded-sm hover:bg-[#FFFFFF] transition-colors"
                  >
                    <UserCircle size={22} weight="regular" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="min-w-[200px]">
                  <DropdownMenuLabel className="font-mono-time text-xs uppercase text-muted-foreground">{user.email}</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate("/profile")} data-testid="menu-profile">
                    <GearSix size={16} className="mr-2" /> Profile & settings
                  </DropdownMenuItem>
                  {user.role === "admin" && (
                    <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="menu-admin">
                      <ShieldStar size={16} className="mr-2" /> Admin panel
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={logout} data-testid="menu-logout">
                    <SignOut size={16} className="mr-2" /> Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Button size="sm" variant="outline" onClick={() => navigate("/login")} data-testid="header-signin-btn" className="rounded-sm">
                <SignIn size={16} className="mr-1" /> Sign in
              </Button>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 pb-[calc(7rem+env(safe-area-inset-bottom))] pt-4">
        <Outlet />
      </main>

      <InstallPrompt />

      <nav
        data-testid="bottom-nav"
        className="fixed bottom-0 left-0 right-0 z-40 bg-[#FFFFFF]/90 backdrop-blur-xl border-t border-[#E5E9F0] pb-[env(safe-area-inset-bottom)]"
      >
        <div className="max-w-3xl mx-auto grid grid-cols-6">          {NAV.map(({ to, label, icon: Icon, testid }) => {
            const isNetwork = to === "/networking";
            const showBadge = isNetwork && unreadDM > 0;
            return (
            <NavLink
              key={to}
              to={to}
              data-testid={testid}
              className={({ isActive }) =>
                `flex flex-col items-center justify-center gap-0.5 py-2 min-h-[56px] transition-colors ${
                  isActive ? "text-[#000000]" : "text-[#5a5a5a] hover:text-foreground"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <span className="relative">
                    <Icon size={22} weight={isActive ? "fill" : "regular"} />
                    {showBadge && (
                      <span
                        data-testid="nav-network-unread-badge"
                        className="absolute -top-1 -right-2 min-w-[18px] h-[18px] px-1 rounded-full bg-[#E0245E] text-[#FFFFFF] text-[10px] font-bold leading-[18px] text-center"
                      >
                        {unreadDM > 9 ? "9+" : unreadDM}
                      </span>
                    )}
                  </span>
                  <span className="text-[10px] font-medium tracking-wide uppercase">{label}</span>
                </>
              )}
            </NavLink>
          );})}
        </div>
      </nav>
    </div>
  );
}
